import { UseQueryResult } from '@tanstack/react-query';
import { pickDefaultModelResult } from '@upright/common/model/data-point-pickers';
import { ByEntityId, ModelEntityType } from '@upright/common/model/input/types';
import {
  CoverageClass,
  ModelEntityWithScores,
  ModelResultWithScores,
} from '@upright/common/model/output/scores/model-entity-data/types';
import { GetSdgAlignmentsRequestParams } from '@upright/common/model/output/scores/sdg-alignment/api-types';
import {
  SdgAlignmentsBySdgAndLevel,
  SdgAlignmentsLayout,
  SdgUnitsMode,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import { getMaxAlignment } from '@upright/common/model/output/scores/sdg-alignment/utils';
import { useFetchSdgAlignments } from '@upright/ui/api/model/output/scores/sdg-alignments';
import { createContext, useContext } from 'react';

type SupportedEntityTypes = ModelEntityType.COMPANY | ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;

export interface SdgAlignmentsContext {
  entityType: SupportedEntityTypes;
  entity: ModelEntityWithScores | undefined;
  sdgAlignmentsRequest: UseQueryResult<SdgAlignmentsBySdgAndLevel, Error>;
  maxAlignment: number;
  entityId: string;
  resultId?: number;
  namesById: ByEntityId<string> | undefined;
  coverageClass?: CoverageClass;
  release: string | undefined;
  version: string | undefined;
  viewOptions: SdgAlignmentsViewOptions;
}

interface ContextProps {
  entityId: string;
  entityType: SupportedEntityTypes;
  entity: ModelEntityWithScores | undefined;
  namesById?: ByEntityId<string>;
  resultId?: number;
  viewOptions: SdgAlignmentsViewOptions;
}

/**
 * Most of these are optional, so the default behavior should happen
 * when they are not given.
 */
interface SdgAlignmentsViewOptions {
  layout?: SdgAlignmentsLayout;
  disableInteraction?: boolean;
  hideLegendBorders?: boolean;
  showModelVersionText?: boolean;
  sdgIdsToShow?: 'all' | 'nonEmpty';
  showBottomBorder?: boolean;
  hideInfoBox?: boolean;
  unitsMode: SdgUnitsMode;
  hideRenderViewButton?: boolean;
}

export function useSdgAlignmentsContextBuilder(props: ContextProps): SdgAlignmentsContext {
  // TODO: remove model results from context. Consumer can call useGetModelEntityScoreData directly
  const modelResults = props.entity?.modelResults;
  const modelResult: ModelResultWithScores | undefined =
    modelResults && (modelResults.find(d => d.resultId === props.resultId) || pickDefaultModelResult(modelResults));

  // TODO: Move data fetching away from this context, and use the context only for UI configuration params
  const sdgAlignmentsRequest = useFetchSdgAlignments({
    entityId: props.entityId,
    entityType: props.entityType,
    resultOrVariantId: modelResult?.resultId,
    release: modelResult?.release,
  } as GetSdgAlignmentsRequestParams);

  return {
    entity: props.entity,
    entityType: props.entityType,
    maxAlignment: getMaxAlignment(sdgAlignmentsRequest.data),
    sdgAlignmentsRequest,
    namesById: props.namesById,
    resultId: modelResult?.resultId,
    entityId: props.entityId,
    coverageClass: modelResult?.coverageClass,
    viewOptions: props.viewOptions,
    release: modelResult?.release,
    version: modelResult?.version,
  };
}

const SdgAlignmentsContext = createContext<SdgAlignmentsContext>({} as SdgAlignmentsContext);

export function useSdgAlignmentsContext() {
  return useContext(SdgAlignmentsContext);
}

export const SdgAlignmentsContextProvider = SdgAlignmentsContext.Provider;
